import { useEffect, useState } from 'react';
import clsx from 'clsx';

import { useAchievementsStore } from '@/entities/achievement';
import type { Achievement } from '@/entities/achievement';
import { useSessionStore } from '@/entities/session';
import { formatScore, openLink } from '@/shared/lib';
import { RADIUS, Skeleton, Squircle } from '@/shared/ui';

import { conditionLabel, segmentsOf } from '../model/condition-label';

import styles from './AchievementsList.module.scss';

/**
 * Сколько карточек видно, пока список свёрнут (узлы 897:9668 свёрнут /
 * 900:10809 развёрнут). В макете свёрнутое состояние обрезает пятую
 * карточку на середине и накрывает обрезок кнопкой — здесь вместо этого
 * граница проходит по целой карточке: обрезка на произвольной высоте
 * не переживёт реальный текст описаний вместо макетных заглушек.
 */
const PREVIEW_COUNT = 4;

/**
 * «Ачивки» на главном экране (узел 438:48230).
 *
 * Состав приходит с сервера: в макете он зашит пятью карточками, которые
 * уже разошлись с ГДД («Регулярыч» удалён, условия «Капера» другие).
 */
export function AchievementsList() {
  const achievements = useAchievementsStore((state) => state.items);
  const isLoading = useAchievementsStore((state) => state.isLoading);
  const error = useAchievementsStore((state) => state.error);
  const load = useAchievementsStore((state) => state.load);

  useEffect(() => {
    void load();
  }, [load]);

  const [isExpanded, setIsExpanded] = useState(false);

  // Легендарные — вперёд (узел 900:10809: обе легендарные карточки идут
  // первыми). Сортировка стабильна: внутри «легендарных» и внутри обычных
  // порядок остаётся серверным, меняется только сам факт группировки.
  const ordered = achievements
    .map((achievement, index) => ({ achievement, index }))
    .sort((a, b) => {
      const byLegendary =
        Number(b.achievement.isLegendary) - Number(a.achievement.isLegendary);
      return byLegendary !== 0 ? byLegendary : a.index - b.index;
    })
    .map(({ achievement }) => achievement);

  const canCollapse = ordered.length > PREVIEW_COUNT;
  const visible =
    isExpanded || !canCollapse ? ordered : ordered.slice(0, PREVIEW_COUNT);

  return (
    <Squircle cornerRadius={RADIUS.xl} className={styles.root}>
      <h2 className={styles.title}>Ачивки</h2>

      {/* Отдельного класса под ошибку в макете нет, а стили этой задачей
          не трогаем — берём типографику описания карточки. */}
      {error ? (
        <p
          className={styles.description}
          role="alert"
          data-testid="achievements-error"
        >
          {error}
        </p>
      ) : null}

      {isLoading && achievements.length === 0 ? (
        <Skeleton count={3} height={92} />
      ) : null}

      {visible.map((achievement) => (
        <AchievementCard key={achievement.code} achievement={achievement} />
      ))}

      {canCollapse ? (
        <Squircle asChild cornerRadius={RADIUS.xl}>
          <button
            type="button"
            className={styles.toggle}
            onClick={() => {
              setIsExpanded((value) => !value);
            }}
            data-testid="achievements-toggle"
          >
            {isExpanded ? 'Скрыть' : 'Смотреть ещё'}
          </button>
        </Squircle>
      ) : null}
    </Squircle>
  );
}

function AchievementCard({ achievement }: { achievement: Achievement }) {
  // Ссылка на СВОЙ канал приходит в сводке игрока — у условия её нет
  const ownChannelUrl = useSessionStore(
    (state) => state.user?.channelUrl ?? null,
  );
  // Получена — Очки, Фрибеты, их значки и подписи зелёные; в процессе —
  // те же сущности обычным (белым) цветом. Раньше подсветку включало
  // `countsForSeasonFinal` — это про то, участвует ли награда в Финальном
  // розыгрыше, а не про то, получена ли Ачивка, и от прогресса не зависело
  // вовсе.
  const isCompleted = achievement.status === 'COMPLETED';
  // «Подписаться» (узел 897:9681) — у незавершённого условия подписки
  // на канал: у «Фаната» в демо-данных оно уже выполнено, кнопка
  // не появляется, регрессии нет.
  //
  // Каналов два вида: свой (ссылка приходит в сводке игрока) и партнёрский
  // у «В эфире» — его знает только сервер и присылает прямо в условии.
  const subscribeCondition = achievement.conditions.find(
    (condition) =>
      (condition.type === 'CHANNEL_SUBSCRIPTION' ||
        condition.type === 'EXTERNAL_CHANNEL_SUBSCRIPTION') &&
      !condition.isCompleted,
  );
  const subscribeUrl = subscribeCondition?.channelUrl ?? ownChannelUrl;

  return (
    <article
      className={styles.card}
      data-testid={`achievement-${achievement.code}`}
    >
      <div className={styles.cardHeader}>
        <h3
          className={clsx(
            styles.cardTitle,
            // Название и метка легендарной Ачивки — оба жёлтые (узел
            // 897:9674): без этого класса заголовок остаётся белым,
            // как у обычной карточки.
            achievement.isLegendary && styles.cardTitleLegendary,
          )}
        >
          {achievement.title}
        </h3>
        {achievement.isLegendary ? (
          <span
            className={styles.legendaryLabel}
            data-testid={`achievement-${achievement.code}-legendary`}
          >
            Легендарная
          </span>
        ) : null}
      </div>
      <p className={styles.description}>{achievement.description}</p>

      {achievement.conditions.map((condition) => {
        const label = conditionLabel(condition, achievement.conditions.length);
        const { segments, filled } = segmentsOf(condition);

        return (
          <div className={styles.group} key={condition.type}>
            {label ? <p className={styles.groupLabel}>{label}</p> : null}
            <div
              className={styles.segments}
              role="progressbar"
              aria-valuenow={condition.currentValue}
              aria-valuemax={condition.targetValue}
              // Grid вместо flex: при полусотне делений на ~360px (3px
              // каждое) flex:1 даёт заметный на глаз разнобой в ширине —
              // flex-basis:0% округляется в целые device-пиксели на
              // элемент незаметно при больших долях и очень заметно при
              // настолько маленьких. У grid-колонок то же округление
              // устойчивее (меньше разброс между соседними треками).
              style={{ ['--segment-count' as string]: segments }}
            >
              {Array.from({ length: segments }, (_, segmentIndex) => (
                <span
                  key={segmentIndex}
                  className={clsx(
                    styles.segment,
                    segmentIndex < filled && styles.segmentFilled,
                  )}
                />
              ))}
            </div>
          </div>
        );
      })}

      <div
        className={clsx(
          styles.reward,
          isCompleted && styles.rewardHighlight,
          subscribeCondition && styles.rewardWithCta,
        )}
      >
        {/* Два счётчика в строку (узел 1000:7631): Фрибеты первыми,
            потом Очки. Фрибетов у большинства Ачивок нет — тогда нет
            и счётчика, а не «0»: ноль читался бы как обещанная награда. */}
        <div className={styles.rewardCounters}>
          {achievement.rewardFreebets > 0 ? (
            <span
              className={styles.rewardValue}
              data-testid={`achievement-${achievement.code}-freebets`}
            >
              <img
                src={
                  isCompleted
                    ? '/images/icons/freebet-light-green.svg'
                    : '/images/icons/freebet-light.svg'
                }
                alt=""
                aria-hidden
                className={styles.fireIcon}
              />
              {formatScore(achievement.rewardFreebets)}
            </span>
          ) : null}
          <span className={styles.rewardValue}>
            <img
              src={
                isCompleted
                  ? '/images/icons/fire-fill-outline-green.svg'
                  : '/images/icons/fire-fill-outline.svg'
              }
              alt=""
              aria-hidden
              className={styles.fireIcon}
            />
            {formatScore(achievement.rewardPoints)}
            {/* Подпись по макету (узел 438:48232): игроку важно, что Ачивка
                даёт, а не как это поле называется у нас внутри */}
            {achievement.countsForSeasonFinal ? (
              <span className={styles.rewardLabel}>призы с розыгрышей</span>
            ) : null}
          </span>
        </div>

        {/* Адрес зависит от условия: партнёрский канал приходит в нём
            самом, свой — из сводки игрока. Ссылки нет (канал не настроен)
            — кнопка не появляется вовсе: мёртвая кнопка хуже её отсутствия */}
        {subscribeCondition && subscribeUrl ? (
          <Squircle asChild cornerRadius={RADIUS.lg}>
            <button
              type="button"
              className={styles.subscribeCta}
              data-testid={`achievement-${achievement.code}-subscribe`}
              onClick={() => {
                openLink(subscribeUrl);
              }}
            >
              Подписаться
            </button>
          </Squircle>
        ) : null}
      </div>
    </article>
  );
}
